import React, { useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { getCountryByCode } from '../data/countries';
import { Mail, Trash2, Edit3, MessageSquare, Send, ChevronLeft, X, Loader2, Search, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessages({ messages, fetchData }) {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const searchTimeoutRef = useRef(null);

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [messageSubTab, setMessageSubTab] = useState('inbox');

  // Compose state
  const [companySearch, setCompanySearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeContent, setComposeContent] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [msgMode, setMsgMode] = useState('select');
  const [msgTarget, setMsgTarget] = useState('all');
  const [msgRecipientCount, setMsgRecipientCount] = useState(0);
  const [msgCountLoading, setMsgCountLoading] = useState(false);
  const [msgCompanies, setMsgCompanies] = useState([]);
  const [selectedMsgIds, setSelectedMsgIds] = useState([]);
  const [msgCompanySearch, setMsgCompanySearch] = useState('');

  // All messages monitor
  const [allMessages, setAllMessages] = useState([]);
  const [allMsgTotal, setAllMsgTotal] = useState(0);
  const [allMsgPage, setAllMsgPage] = useState(1);
  const [allMsgSearch, setAllMsgSearch] = useState('');
  const [allMsgLoading, setAllMsgLoading] = useState(false);
  const [allMsgSelected, setAllMsgSelected] = useState(null);

  const receivedMessages = messages.filter(m => m.receiver_id === user?.id);
  const sentMessages = messages.filter(m => m.sender_id === user?.id);
  const unreadCount = receivedMessages.filter(m => !m.read).length;

  const markAsRead = async (messageId) => {
    try {
      await axios.patch(`${API}/messages/${messageId}/read`, {});
    } catch (error) { console.error(error); }
  };

  const handleReply = async () => {
    if (!selectedMessage || !replyContent.trim()) return;
    try {
      await axios.post(`${API}/messages`, { receiver_id: selectedMessage.sender_id, subject: `${language === 'ar' ? 'رد: ' : 'Re: '}${selectedMessage.subject}`, content: replyContent });
      toast.success(language === 'ar' ? 'تم إرسال الرد بنجاح' : 'Reply sent successfully');
      setReplyContent(''); setSelectedMessage(null); fetchData();
    } catch (error) { console.error(error); }
  };

  const handleCompanySearch = (value) => {
    setCompanySearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (value.length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try { const { data } = await axios.get(`${API}/admin/companies/search?q=${encodeURIComponent(value)}`); setSearchResults(data); }
      catch (e) { console.error(e); }
      finally { setSearchLoading(false); }
    }, 300);
  };

  const handleSendToCompany = async () => {
    let payload = { subject: composeSubject, content: composeContent };
    if (msgMode === 'filter') { payload.account_type = msgTarget; }
    else {
      if (selectedMsgIds.length === 0) return;
      payload.receiver_company_ids = selectedMsgIds;
    }
    if (!composeSubject.trim() || !composeContent.trim()) return;
    setComposeSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/messages`, payload);
      const count = data.sent || 1;
      toast.success(language === 'ar' ? `تم إرسال الرسالة إلى ${count} شركة بنجاح` : `Message sent to ${count} company(s) successfully`);
      setSelectedMsgIds([]); setComposeSubject(''); setComposeContent(''); setCompanySearch(''); setSearchResults([]);
      setMessageSubTab('sent'); fetchData();
    } catch (error) { console.error(error); toast.error(language === 'ar' ? 'فشل إرسال الرسالة' : 'Failed to send message'); }
    finally { setComposeSending(false); }
  };

  const fetchMsgRecipientCount = async (target) => {
    setMsgCountLoading(true);
    try { const { data } = await axios.get(`${API}/admin/broadcast/recipients-count?account_type=${target}`); setMsgRecipientCount(data.count); }
    catch (e) { console.error(e); } finally { setMsgCountLoading(false); }
  };

  const fetchMsgCompanies = async (target) => {
    try { const { data } = await axios.get(`${API}/admin/broadcast/companies?account_type=${target}`); setMsgCompanies(data); }
    catch (e) { console.error(e); }
  };

  const handleMsgTargetChange = (val) => { setMsgTarget(val); fetchMsgRecipientCount(val); if (msgMode === 'select') fetchMsgCompanies(val); };
  const toggleMsgCompany = (id) => { setSelectedMsgIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]); };
  const filteredMsgCompanies = msgCompanies.filter(c => !msgCompanySearch || c.name.toLowerCase().includes(msgCompanySearch.toLowerCase()) || c.phone.includes(msgCompanySearch));

  const fetchAllMessages = async (page = 1, search = '') => {
    setAllMsgLoading(true);
    try { const { data } = await axios.get(`${API}/admin/all-messages?page=${page}&page_size=20${search ? `&q=${encodeURIComponent(search)}` : ''}`); setAllMessages(data.items || []); setAllMsgTotal(data.total || 0); setAllMsgPage(page); }
    catch (e) { console.error(e); } finally { setAllMsgLoading(false); }
  };

  const accountTypeButtons = [
    { value: 'all', labelAr: 'جميع الشركات', labelEn: 'All Companies' },
    { value: 'seller', labelAr: 'البائعين', labelEn: 'Sellers' },
    { value: 'buyer', labelAr: 'المشترين', labelEn: 'Buyers' },
    { value: 'both', labelAr: 'بائع ومشتري', labelEn: 'Both' },
  ];

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex gap-2 mb-5 bg-gray-100 rounded-lg p-1" data-testid="message-subtabs">
        {[
          { key: 'inbox', icon: <Mail size={16} />, label: language === 'ar' ? 'الوارد' : 'Inbox', badge: unreadCount > 0 ? unreadCount : null },
          { key: 'sent', icon: <Send size={16} />, label: language === 'ar' ? 'المرسل' : 'Sent', badge: sentMessages.length },
          { key: 'compose', icon: <Edit3 size={16} />, label: language === 'ar' ? 'رسالة جديدة' : 'New Message' },
          { key: 'all', icon: <Eye size={16} />, label: language === 'ar' ? 'كل الرسائل' : 'All Messages' },
        ].map(tab => (
          <button key={tab.key} onClick={() => { setMessageSubTab(tab.key); if (tab.key === 'all') fetchAllMessages(1, allMsgSearch); }}
            data-testid={`subtab-${tab.key === 'all' ? 'all-messages' : tab.key}`}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-all ${messageSubTab === tab.key ? 'bg-white text-accent shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            {tab.icon} {tab.label}
            {tab.badge != null && <span className={`px-1.5 py-0.5 text-xs rounded-full ${tab.key === 'inbox' ? 'bg-accent text-white' : 'bg-gray-200 text-gray-600'}`}>{tab.badge}</span>}
          </button>
        ))}
      </div>

      {/* Cleanup button */}
      <div className="flex justify-end mb-3">
        <button onClick={async () => {
          if (!window.confirm(language === 'ar' ? 'هل تريد حذف جميع الرسائل التجريبية (TEST)؟' : 'Delete all test messages?')) return;
          try { const { data } = await axios.delete(`${API}/admin/messages/cleanup-test`); toast.success(language === 'ar' ? `تم حذف ${data.deleted_messages} رسالة و ${data.deleted_notifications} إشعار تجريبي` : `Deleted ${data.deleted_messages} messages & ${data.deleted_notifications} notifications`); fetchData(); }
          catch (e) { toast.error(language === 'ar' ? 'فشل الحذف' : 'Cleanup failed'); }
        }} data-testid="cleanup-test-messages-btn" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
          <Trash2 size={14} /> {language === 'ar' ? 'حذف رسائل TEST' : 'Delete TEST messages'}
        </button>
      </div>

      {/* Inbox */}
      {messageSubTab === 'inbox' && (
        <div className="space-y-3">
          {receivedMessages.length === 0 ? (
            <div className="bg-white p-16 border border-border rounded-xl text-center" data-testid="no-admin-messages">
              <Mail size={48} className="mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-muted-foreground">{language === 'ar' ? 'لا توجد رسائل واردة' : 'No incoming messages'}</p>
            </div>
          ) : receivedMessages.map((message) => (
            <div key={message.id} className={`bg-white p-5 border rounded-xl cursor-pointer hover:shadow-sm transition-all ${!message.read ? 'border-accent bg-accent/5' : 'border-border'}`}
              onClick={() => { setSelectedMessage({...message, _isSent: false}); if (!message.read) markAsRead(message.id); }} data-testid={`admin-message-${message.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className={`text-base ${!message.read ? 'font-bold' : 'font-medium'} text-primary mb-1`}>{message.subject}</h3>
                  <p className="text-sm text-muted-foreground">{language === 'ar' ? 'من' : 'From'}: <span className="font-medium text-foreground">{message.sender_name || message.sender_id}</span></p>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{message.content}</p>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap ml-3">{new Date(message.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sent */}
      {messageSubTab === 'sent' && (
        <div className="space-y-3">
          {sentMessages.length === 0 ? (
            <div className="bg-white p-16 border border-border rounded-xl text-center"><Mail size={48} className="mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">{language === 'ar' ? 'لا توجد رسائل مرسلة' : 'No sent messages'}</p></div>
          ) : sentMessages.map((message) => (
            <div key={message.id} className="bg-white p-5 border border-border rounded-xl cursor-pointer hover:shadow-sm transition-all"
              onClick={() => setSelectedMessage({...message, _isSent: true})} data-testid={`admin-message-${message.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-medium text-primary mb-1">{message.subject}</h3>
                  <p className="text-sm text-muted-foreground">{language === 'ar' ? 'إلى' : 'To'}: <span className="font-medium text-foreground">{message.receiver_name || message.receiver_id}</span></p>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap ml-3">{new Date(message.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* All Messages Monitor */}
      {messageSubTab === 'all' && (
        <div data-testid="all-messages-section">
          <div className="relative mb-4">
            <Search size={18} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
            <input type="text" value={allMsgSearch} onChange={(e) => { setAllMsgSearch(e.target.value); if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); searchTimeoutRef.current = setTimeout(() => fetchAllMessages(1, e.target.value), 400); }}
              placeholder={language === 'ar' ? 'بحث في الرسائل (الاسم، الموضوع، المحتوى)...' : 'Search messages...'} data-testid="all-msg-search"
              className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm`} />
          </div>
          <p className="text-sm text-muted-foreground mb-3" data-testid="all-msg-count">{language === 'ar' ? `إجمالي الرسائل: ${allMsgTotal}` : `Total messages: ${allMsgTotal}`}</p>
          {allMsgLoading ? <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-accent" /></div>
           : allMessages.length === 0 ? <div className="bg-white p-12 border border-border rounded-xl text-center"><MessageSquare size={40} className="mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">{language === 'ar' ? 'لا توجد رسائل' : 'No messages found'}</p></div>
           : <div className="space-y-2">{allMessages.map((msg) => (
            <div key={msg.id} onClick={() => setAllMsgSelected(allMsgSelected?.id === msg.id ? null : msg)}
              className={`bg-white border rounded-xl cursor-pointer hover:shadow-sm transition-all ${!msg.read ? 'border-accent/30 bg-accent/5' : 'border-border'} ${allMsgSelected?.id === msg.id ? 'ring-2 ring-accent/40' : ''}`} data-testid={`all-msg-${msg.id}`}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h4 className="text-sm font-bold text-primary flex-1 truncate">{msg.subject}</h4>
                  <div className="flex items-center gap-2 shrink-0">
                    {!msg.read && <span className="w-2 h-2 bg-accent rounded-full" />}
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(msg.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-medium">{msg.sender_name || msg.sender_id?.slice(-6)}</span>
                  <ChevronLeft size={12} className="text-muted-foreground/50" />
                  <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded font-medium">{msg.receiver_name || msg.receiver_id?.slice(-6)}</span>
                </div>
              </div>
              {allMsgSelected?.id === msg.id && <div className="px-4 pb-4 pt-1 border-t border-border"><p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{msg.content}</p></div>}
            </div>
          ))}</div>}
          {allMsgTotal > 20 && (
            <div className="flex items-center justify-center gap-3 mt-5">
              <button onClick={() => fetchAllMessages(allMsgPage - 1, allMsgSearch)} disabled={allMsgPage <= 1} data-testid="all-msg-prev" className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 disabled:opacity-40">{language === 'ar' ? 'السابق' : 'Previous'}</button>
              <span className="text-sm text-muted-foreground">{language === 'ar' ? `صفحة ${allMsgPage} من ${Math.ceil(allMsgTotal / 20)}` : `Page ${allMsgPage} of ${Math.ceil(allMsgTotal / 20)}`}</span>
              <button onClick={() => fetchAllMessages(allMsgPage + 1, allMsgSearch)} disabled={allMsgPage >= Math.ceil(allMsgTotal / 20)} data-testid="all-msg-next" className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 disabled:opacity-40">{language === 'ar' ? 'التالي' : 'Next'}</button>
            </div>
          )}
        </div>
      )}

      {/* Compose */}
      {messageSubTab === 'compose' && (
        <div className="bg-white border border-border rounded-xl p-6" data-testid="compose-message-section">
          <div className="flex gap-2 mb-5">
            <button onClick={() => { setMsgMode('filter'); setSelectedMsgIds([]); fetchMsgRecipientCount(msgTarget); }} data-testid="msg-mode-filter" className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all ${msgMode === 'filter' ? 'bg-primary text-white border-primary' : 'bg-white text-foreground border-border hover:border-primary/50'}`}>{language === 'ar' ? 'إرسال حسب الفئة' : 'Send by Category'}</button>
            <button onClick={() => { setMsgMode('select'); setSelectedMsgIds([]); fetchMsgCompanies(msgTarget); fetchMsgRecipientCount(msgTarget); }} data-testid="msg-mode-select" className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all ${msgMode === 'select' ? 'bg-primary text-white border-primary' : 'bg-white text-foreground border-border hover:border-primary/50'}`}>{language === 'ar' ? 'اختيار شركات محددة' : 'Select Companies'}</button>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-muted-foreground mb-2">{language === 'ar' ? 'نوع الحساب' : 'Account Type'}</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {accountTypeButtons.map(opt => (
                <button key={opt.value} onClick={() => handleMsgTargetChange(opt.value)} data-testid={`msg-target-${opt.value}`}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${msgTarget === opt.value ? 'bg-accent text-white border-accent shadow-sm' : 'bg-white text-foreground border-border hover:border-accent/50'}`}>
                  {language === 'ar' ? opt.labelAr : opt.labelEn}
                </button>
              ))}
            </div>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="msg-recipient-count">
              {msgCountLoading ? (language === 'ar' ? 'جاري التحميل...' : 'Loading...') : msgMode === 'select' && selectedMsgIds.length > 0 ? (language === 'ar' ? `تم اختيار ${selectedMsgIds.length} شركة` : `${selectedMsgIds.length} companies selected`) : (language === 'ar' ? `عدد المستلمين: ${msgRecipientCount} شركة` : `Recipients: ${msgRecipientCount} companies`)}
            </p>
          </div>
          {msgMode === 'select' && (
            <div className="mb-5 border border-border rounded-lg overflow-hidden" data-testid="msg-company-list">
              <div className="p-3 bg-gray-50 border-b border-border flex flex-col sm:flex-row gap-2">
                <div className="flex-1 relative">
                  <Search size={16} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                  <input type="text" value={msgCompanySearch} onChange={(e) => setMsgCompanySearch(e.target.value)} placeholder={language === 'ar' ? 'بحث عن شركة...' : 'Search company...'} data-testid="msg-company-search"
                    className={`w-full px-4 py-2 ${language === 'ar' ? 'pr-9' : 'pl-9'} border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent`} />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setSelectedMsgIds(filteredMsgCompanies.map(c => c.id))} data-testid="msg-select-all" className="px-3 py-2 text-xs font-medium bg-accent/10 text-accent rounded-lg hover:bg-accent/20 transition-colors">{language === 'ar' ? 'تحديد الكل' : 'Select All'}</button>
                  <button onClick={() => setSelectedMsgIds([])} data-testid="msg-deselect-all" className="px-3 py-2 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300 transition-colors">{language === 'ar' ? 'إلغاء الكل' : 'Deselect All'}</button>
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto divide-y divide-border">
                {filteredMsgCompanies.length === 0 ? <p className="text-center py-6 text-sm text-muted-foreground">{language === 'ar' ? 'لا توجد شركات' : 'No companies found'}</p>
                 : filteredMsgCompanies.map(c => (
                  <label key={c.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors">
                    <input type="checkbox" checked={selectedMsgIds.includes(c.id)} onChange={() => toggleMsgCompany(c.id)} data-testid={`msg-check-${c.id}`} className="w-4 h-4 rounded border-border text-accent focus:ring-accent" />
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-foreground truncate">{c.name}</p><p className="text-xs text-muted-foreground" dir="ltr">{c.phone}</p></div>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{c.account_type === 'seller' ? (language === 'ar' ? 'بائع' : 'Seller') : c.account_type === 'buyer' ? (language === 'ar' ? 'مشتري' : 'Buyer') : (language === 'ar' ? 'بائع ومشتري' : 'Both')}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-4">
            <div><label className="block text-sm font-semibold text-muted-foreground mb-1.5">{language === 'ar' ? 'الموضوع' : 'Subject'}</label><input type="text" value={composeSubject} onChange={(e) => setComposeSubject(e.target.value)} placeholder={language === 'ar' ? 'اكتب موضوع الرسالة...' : 'Message subject...'} data-testid="compose-subject-input" className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent" /></div>
            <div><label className="block text-sm font-semibold text-muted-foreground mb-1.5">{language === 'ar' ? 'محتوى الرسالة' : 'Message Content'}</label><textarea value={composeContent} onChange={(e) => setComposeContent(e.target.value)} placeholder={language === 'ar' ? 'اكتب رسالتك هنا...' : 'Write your message here...'} data-testid="compose-content-input" rows={5} className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent resize-none" /></div>
            <button onClick={handleSendToCompany} disabled={composeSending || !composeSubject.trim() || !composeContent.trim() || (msgMode === 'select' && selectedMsgIds.length === 0) || (msgMode === 'filter' && msgRecipientCount === 0)} data-testid="compose-send-btn"
              className="w-full flex items-center justify-center gap-2 py-3 bg-accent text-white rounded-lg hover:bg-accent/90 font-semibold transition-all disabled:opacity-50">
              {composeSending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {msgMode === 'filter' ? (language === 'ar' ? `إرسال إلى ${msgRecipientCount} شركة` : `Send to ${msgRecipientCount} companies`) : selectedMsgIds.length > 0 ? (language === 'ar' ? `إرسال إلى ${selectedMsgIds.length} شركة` : `Send to ${selectedMsgIds.length} companies`) : (language === 'ar' ? 'إرسال الرسالة' : 'Send Message')}
            </button>
          </div>
        </div>
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setSelectedMessage(null); setReplyContent(''); }} data-testid="admin-message-modal-overlay">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-message-modal">
            <div className="p-6 border-b border-border">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-xl font-heading font-bold text-primary">{selectedMessage.subject}</h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    {selectedMessage._isSent ? <>{language === 'ar' ? 'إلى' : 'To'}: <span className="font-medium text-foreground">{selectedMessage.receiver_name}</span></> : <>{language === 'ar' ? 'من' : 'From'}: <span className="font-medium text-foreground">{selectedMessage.sender_name}</span></>}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{new Date(selectedMessage.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <button onClick={() => { setSelectedMessage(null); setReplyContent(''); }} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} className="text-muted-foreground" /></button>
              </div>
            </div>
            <div className="p-6">
              <p className="text-foreground leading-relaxed whitespace-pre-wrap mb-6">{selectedMessage.content}</p>
              {!selectedMessage._isSent && (
                <div className="border-t border-border pt-4">
                  <h3 className="text-sm font-semibold text-muted-foreground mb-2">{language === 'ar' ? 'الرد' : 'Reply'}</h3>
                  <textarea value={replyContent} onChange={(e) => setReplyContent(e.target.value)} data-testid="admin-reply-input" rows={3} className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent mb-3" placeholder={language === 'ar' ? 'اكتب ردك هنا...' : 'Write your reply...'} />
                  <button onClick={handleReply} data-testid="admin-send-reply-btn" className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent text-white rounded-lg hover:bg-accent/90 font-semibold transition-all"><Send size={18} />{language === 'ar' ? 'إرسال الرد' : 'Send Reply'}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
